import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponse, DefaultResponseWithData } from "../models/system";
import { ISendingDomain } from "../models/sendingDomain/ISendingDomain";
import apiConfig from "../utils/apiConfig";
import useDefaultCRUDHandlers from "../hooks/useDefaultCRUDHandlers";

const queryKey = ["sendingDomains"];

export default function useSendingDomains() {
  const { get, post, del } = useApiHelper(apiConfig("useSendingDomains"));
  const queryClient = useQueryClient();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Sending domain");

  const domains = useQuery({
    queryKey,
    queryFn: () => get<DefaultResponseWithData<ISendingDomain[]>>("/sendingdomain"),
  });

  const add = useMutation({
    mutationFn: (domain: string) =>
      post<{ domain: string }, DefaultResponseWithData<ISendingDomain>>(
        "/sendingdomain",
        { domain }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      saveHandlers.onSuccess();
    },
    onError: saveHandlers.onError,
  });

  const verify = useMutation({
    mutationFn: (id: string) =>
      post<Record<string, never>, DefaultResponseWithData<ISendingDomain>>(
        `/sendingdomain/${id}/verify`,
        {}
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => del<DefaultResponse>(`/sendingdomain/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      deleteHandlers.onSuccess();
    },
    onError: deleteHandlers.onError,
  });

  return {
    domains,
    add,
    verify,
    remove,
  };
}
